/* eslint-disable react/jsx-key,react/prop-types */
import React, { PureComponent } from 'react';
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types';
import _ from 'lodash';
import debounce from 'lodash.debounce';
import { withRouter } from 'react-router';
import qs from 'query-string';
import { addEventListener } from 'consolidated-events';
import ProductPane, { staticTextKeys as productPaneStaticTextKeys } from 'components/product/ProductPane/ProductPane';
import {
  Small, Freeze, IdleTimer, timeoutPreset, HtmlParser,
} from 'components/common';
import Experiment from 'components/abTest/Experiment';
import { activtateOptimize, getRuntimeEnvVariables, isShowExitPopup } from 'helpers/Utils';
import { CustomerProfileContext } from 'containers/withCustomerProfileContext';
import withUserAgent from 'containers/withUserAgent';
import withApplicationConfig from 'containers/withApplicationConfig';
import NoResultsIndicator from './NoResultsIndicator';
import { sortResults, isProductPaneUseFeature } from './ResultsPageUtils';
import './ResultsListView.scss';

export const staticTextKeys = [
  'noResultsTitle',
  'noResultsSubHeader',
  'noResultsReset',
  'showMoreResultsButtonLabel',
  ...productPaneStaticTextKeys,
];

class ResultsListView extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      page: 0,
    };

    this.listViewRef = React.createRef();
    this.handleInfiniteScroll = this.handleInfiniteScroll.bind(this);
    this.incrementPage = this.incrementPage.bind(this);
    this.debounceIncrementPage = debounce(this.incrementPage, 300, {
      leading: true,
      trailing: false,
    }).bind(this);
    this.getSearchParamsNumberInitialResults = this.getSearchParamsNumberInitialResults.bind(this);
    const mobileExitpopUpConfig = props.exitPopupOrder.filter(p => p.name === 'mobileIdlePopup');
    this.timer = mobileExitpopUpConfig.length > 0
    && isShowExitPopup(props.userAgent, props.verticalEnum, mobileExitpopUpConfig[0])
      ? new IdleTimer({
        timeout: mobileExitpopUpConfig[0].timeout
          ? mobileExitpopUpConfig[0].timeout : timeoutPreset.mobileModal,
      }) : undefined;
  }

  componentDidMount() {
    this.removeScrollListener = addEventListener(
      window,
      'scroll',
      this.handleInfiniteScroll,
    );
    activtateOptimize();
  }

  componentDidUpdate() {
    activtateOptimize();
  }

  componentWillUnmount() {
    this.timer?.cleanUp();
    this.removeScrollListener();
  }

  // This param is used only by automation test
  // to get rid of pagination and get all results at once
  getSearchParamsNumberInitialResults() {
    const { location } = this.props;
    const searchParams = qs.parse(location.search);
    return searchParams?.initialResults && parseInt(searchParams?.initialResults, 10);
  }

  handleInfiniteScroll() {
    const { current: listViewEl } = this.listViewRef;
    if (!listViewEl) return;

    const listViewBottom = listViewEl.getBoundingClientRect().bottom;
    const windowHeight = window.innerHeight;

    // increment page when scrolled to list view bottom
    if (listViewBottom > windowHeight) return;

    // Debounce because we want to wait for the new data render instead of keep
    // incrementing the page number
    this.debounceIncrementPage();
  }

  incrementPage() {
    this.setState(({ page }) => ({
      page: page + 1,
    }));
  }

  setScaleoParamToCTAButtonUrl(results) {
    const { location } = this.props;
    const { search } = location;
    const { clkid: scaleoClickId, clktype: scaleoClickType } = qs.parse(search);

    if (scaleoClickId && scaleoClickType) {
      results?.map((product) => {
        if (product?.ctaButton?.link?.url) {
          let ctaButtonURL = product?.ctaButton?.link?.url;
          if (!ctaButtonURL.includes("clkid") && !ctaButtonURL.includes("clktype")) {
            ctaButtonURL += `&clkid=${   scaleoClickId}`;
            ctaButtonURL += `&clktype=${   scaleoClickType}`;
          }
          product.ctaButton.link.url = ctaButtonURL;
        }
      });
    }
    return results;
  }

  render() {
    const { page } = this.state;
    const {
      products,
      numberInitialResults,
      numberMoreResults,
      // TODO(Ryan) will can use from CMS
      // resultsDisclaimerMessage,
      onResetFilters,
      sortByValue,
      // app config
      staticText,
      imageDimensions,
      onProductPaneCtaLinkClickWithIdAndProducts,
      locale,
      language,
      disclaimerMessages,
      list,
      verticalEnum,
      vertical,
      allCountries,
      selectedInsuranceType,
      selectedRegion,
      isLearnMore,
      onLearnMoreClick,
      disclaimer,
      recommendationData,
    } = this.props;

    let results = (products) || [];
    results = sortResults(results, sortByValue);

    // This param is used only by automation test
    // to get rid of pagination and get all results at once
    const searchParamsNumberInitialResults = this.getSearchParamsNumberInitialResults();

    const finalNumberInitialResults = searchParamsNumberInitialResults || numberInitialResults;

    // Initial results based on initialMaxResultCount
    const initialResults = results.slice(0, finalNumberInitialResults);

    // Get remaining result chunks based on moreResultCount
    const remainingResultChunks = _.chunk(results
      .slice(finalNumberInitialResults), numberMoreResults);

    // Get paginated result based on page number
    const paginatedResults = initialResults.concat(_.flatten(remainingResultChunks.slice(0, page)));

    // add scaleo param to CTA Button URL
    results = this.setScaleoParamToCTAButtonUrl(results);

    // TODO(Ryan) Set the disclaimer messages in Product Panel and List
    const {
      productDisclaimerMessage,
    } = (disclaimerMessages || {})[language] || {};

    if (!results.length) {
      return (
        <NoResultsIndicator
          onResetFilters={onResetFilters}
          labels={{
            noResultsTitle: staticText.noResultsTitle,
            noResultsSubHeader: staticText.noResultsSubHeader,
            noResultsReset: staticText.noResultsReset,
          }}
          recommendationData={recommendationData}
        />
      );
    }

    return (
      <div className="result-list-view-wrapper">
        <div className="results-list-view" ref={this.listViewRef}>
          {paginatedResults
            .filter(product => product && Object.keys(product).length !== 0)
            .filter(product => product.productName)
            .map((product, index) => (
              <Freeze
                freeze={!(getRuntimeEnvVariables('RUNTIME_PRODUCT_PAGE_NEW_ENTRY_POINT_EXPERIMENT_ID')
                  || getRuntimeEnvVariables('RUNTIME_SS_RESULT_PAGE_LEARN_MORE_EXPERIMENT_ID')
                  || isProductPaneUseFeature(product?.promotional))}
              >
                {/* TODO: Remove after exp end */}
                <Experiment
                  experimentId={getRuntimeEnvVariables('RUNTIME_PRODUCT_PAGE_NEW_ENTRY_POINT_EXPERIMENT_ID')}
                  controlVariantId={getRuntimeEnvVariables('RUNTIME_PRODUCT_PAGE_NEW_ENTRY_POINT_EXPERIMENT_CONTROL')}
                  variantIds={(getRuntimeEnvVariables('RUNTIME_PRODUCT_PAGE_NEW_ENTRY_POINT_EXPERIMENT_VARIANTS') || '').split(',')}
                />
                <CustomerProfileContext.Consumer>
                  {({ showExitModal, setIsMobileIdleModalOpen }) => {
                    if (this.timer) this.timer.onTimeOut = () => setIsMobileIdleModalOpen(true);
                    return (
                      <ProductPane
                        {...product}
                        index={index}
                        imageDimensions={imageDimensions}
                        staticText={staticText}
                        onCtaLinkClick={(e) => {
                          if (onProductPaneCtaLinkClickWithIdAndProducts) {
                            onProductPaneCtaLinkClickWithIdAndProducts(e, product.id, products);
                          }

                          if (product.ctaButton?.link?.linkClass === 'EXTERNAL') {
                            showExitModal(verticalEnum);
                          }
                        }}
                        locale={locale}
                        disclaimer={productDisclaimerMessage}
                        list={list}
                        verticalEnum={verticalEnum}
                        vertical={vertical}
                        language={language}
                        urlAliasInfo={product.functional}
                        allCountries={allCountries}
                        selectedInsuranceType={selectedInsuranceType}
                        selectedRegion={selectedRegion}
                    // not lazy loading image for first 4 products
                        lazyLoadImg={index > 3}
                        isLearnMore={isLearnMore}
                        onLearnMoreClick={(e, color) => {
                          onLearnMoreClick(e, product.id, products, color);
                        }}
                      />
                    );
                  }}
                </CustomerProfileContext.Consumer>
              </Freeze>
            ))}
        </div>

        {disclaimer && (
        <div className="disclaimer">
          <Small secondary><HtmlParser htmlString={disclaimer} /></Small>
        </div>
        )}
      </div>
    );
  }
}

ResultsListView.propTypes = {
  numberInitialResults: number,
  numberMoreResults: number,
  queryRoot: string,
  onResetFilters: func,
  loading: bool,
  sortByValue: string,
  error: shape({
    message: string,
  }),
  products: arrayOf(shape({})),
  imageDimensions: shape({}).isRequired,
  staticText: shape({}),
  onProductPaneCtaLinkClickWithIdAndProducts: func,
  loadingIndicatorLabel: string,
  // resultsDisclaimerMessage: string,
  onIsShowMoreChange: func,
  list: string,
  disclaimerMessages: shape({}),
  locale: string,
  language: string,
  verticalEnum: string,
  vertical: string.isRequired,
  allCountries: arrayOf(shape({})),
  selectedInsuranceType: string,
  selectedRegion: string,
  location: shape({}),
  isLearnMore: bool,
  onLearnMoreClick: func,
  signupExitModal: shape({
    isOpen: bool,
    toggle: func,
  }),
  userAgent: shape({
    isMobile: bool,
  }),
  exitPopupOrder: arrayOf(shape({
    name: string,
    showDesktop: bool,
    showMobile: bool,
    verticals: arrayOf(string),
  })),
  recommendationData: shape({
    title: string,
    href: string,
  }),
};

ResultsListView.defaultProps = {
  numberInitialResults: 0,
  numberMoreResults: 0,
  queryRoot: null,
  onResetFilters: null,
  loading: false,
  error: null,
  sortByValue: null,
  products: null,
  staticText: {},
  onProductPaneCtaLinkClickWithIdAndProducts: null,
  loadingIndicatorLabel: null,
  // resultsDisclaimerMessage: null,
  onIsShowMoreChange: null,
  list: null,
  disclaimerMessages: {},
  locale: 'en-hk',
  language: 'en',
  verticalEnum: null,
  allCountries: [],
  selectedInsuranceType: '',
  selectedRegion: '',
  location: null,
  recommendationData: null,
};

export default withApplicationConfig(withRouter(withUserAgent(ResultsListView)), 'exitPopupOrder');
